import { useState, useEffect } from 'react';
import {
  Card,
  Col,
  Row,
  Form,
  InputGroup,
  Alert,
  Spinner,
  Modal
} from 'react-bootstrap';
import {
  faPhone,
  faMapPin,
  faBriefcase,
  faCertificate,
  faFileAlt,
  faEdit,
  faSave,
  faTimes,
  faDownload,
  faEye,
  faIdCard
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import AvatarUpload from 'components/common/AvatarUpload';
import LocationAutocomplete from 'components/common/LocationAutocomplete';
import { Coordinates } from 'features/location/googleLocation';
import classNames from 'classnames';
import team15 from 'assets/img/team/avatar-rounded.webp';
import {
  getProfile,
  updateProfile,
  downloadDocument,
  viewDocument
} from 'features/profile/profileApi';
import { useAuth } from 'providers/AuthProvider';
import { getAuthSession } from 'features/auth/authSession';

const normalizeUkPhoneLocal = (value: string) => {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('44')) {
    return digits.slice(2);
  }
  if (digits.startsWith('0')) {
    return digits.slice(1);
  }
  return digits;
};


const formatUkPhone = (value: string) => {
  const digits = normalizeUkPhoneLocal(value);
  return digits ? `+44${digits}` : '';
};

const formatDisplayDate = (value?: string | null) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const toInputDate = (value?: string | null) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const LocumProfilePage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [profileData, setProfileData] = useState<any>(null);
  const [formData, setFormData] = useState<any>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [showDocViewer, setShowDocViewer] = useState(false);
  const [selectedCoordinates, setSelectedCoordinates] =
    useState<Coordinates | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});

  const { login } = useAuth();

  // Required fields for locum profile - match registration mandatory fields
  const requiredFields = [
    'name',
    'last_name',
    'phone',
    'qualifications',
    'experience_years',
    'location',
    'address',
    'city',
    'zip_code',
    'dob',
    'gender',
    'qualification_date',
    'independent_prescriber'
  ];

  // Fetch profile data
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await getProfile();
        setProfileData(data);
        setFormData({
          name: data.user.name,
          last_name: data.user.last_name,
          ...data.profile
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load profile');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, []);

  // Handle form input change
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]:
        name.includes('years') || name.includes('distance')
          ? parseFloat(value)
          : value
    });
    // Clear error for this field when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors({
        ...fieldErrors,
        [name]: ''
      });
    }
  };

  // Validate required fields
  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    requiredFields.forEach(field => {
      const value = formData[field];
      // Special handling for independent_prescriber which can be 0 (valid)
      if (field === 'independent_prescriber') {
        if (value === null || value === undefined || value === '') {
          newErrors[field] = 'Independent Prescriber is required';
        }
      }
      else if (!value || (typeof value === 'string' && value.trim() === '')) {
        const fieldLabel = field
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        newErrors[field] = `${fieldLabel} is required`;
      }
    });

    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save
  const handleSave = async () => {
    // Validate form before saving
    if (!validateForm()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      // Include coordinates in form data if selected
      const dataToSave = { ...formData };
      if (dataToSave.phone) {
        dataToSave.phone = formatUkPhone(dataToSave.phone);
      }
      if (dataToSave.ref_PhoneNumber_1 !== undefined) {
        dataToSave.ref_PhoneNumber_1 = formatUkPhone(
          dataToSave.ref_PhoneNumber_1
        );
      }
      if (dataToSave.ref_PhoneNumber_2 !== undefined) {
        dataToSave.ref_PhoneNumber_2 = formatUkPhone(
          dataToSave.ref_PhoneNumber_2
        );
      }
      if (selectedCoordinates) {
        dataToSave.latitude = selectedCoordinates.lat;
        dataToSave.longitude = selectedCoordinates.lng;
      }

      const result = await updateProfile(dataToSave);

      // Get the current session and update it with new user data
      const currentSession = getAuthSession();
      if (currentSession) {
        login({
          token: currentSession.token,
          user: {
            ...currentSession.user,
            name: result.user.name,
            last_name: result.user.last_name
          }
        });
      }

      setSuccess('Profile updated successfully');
      setIsEditing(false);
      setProfileData(result);
      setFormData({
        name: result.user.name,
        last_name: result.user.last_name,
        ...result.profile
      });
      setSelectedCoordinates(null);
      setFieldErrors({});
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setFormData(profileData?.profile || {});
    setIsEditing(false);
    setError(null);
    setSelectedCoordinates(null);
    setFieldErrors({});
  };

  // Handle document download
  const handleDocumentDownload = async (document: any) => {
    try {
      console.log('Starting download for:', document.document_name);
      await downloadDocument(document.document_name);
      console.log('Download successful for:', document.document_name);
      setSuccess('Document downloaded successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : 'Failed to download document';
      console.error('Download error:', errorMsg);
      setError(errorMsg);
    }
  };

  // Handle document view
  const handleDocumentView = (document: any) => {
    viewDocument(document.document_name);
  };

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '400px' }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (!profileData) {
    return <Alert variant="danger">Failed to load profile</Alert>;
  }

  return (
    <div>
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)} dismissible>
          {success}
        </Alert>
      )}

      {/* Profile Header Card */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="align-items-center g-4">
            <Col xs={12} sm="auto">
              <AvatarUpload size="5xl" src={team15} />
            </Col>
            <Col xs={12} sm>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  {isEditing ? (
                    <div className="mb-3">
                      <Row className="g-2">
                        <Col md={6}>
                          <Form.Group className="text-start mb-2">
                            <Form.Label>
                              First Name <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="name"
                              value={formData.name || ''}
                              onChange={handleInputChange}
                              placeholder="First Name"
                              isInvalid={!!fieldErrors.name}
                            />
                            {fieldErrors.name && (
                              <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                                {fieldErrors.name}
                              </Form.Control.Feedback>
                            )}
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="text-start mb-2">
                            <Form.Label>
                              Last Name <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="last_name"
                              value={formData.last_name || ''}
                              onChange={handleInputChange}
                              placeholder="Last Name"
                              isInvalid={!!fieldErrors.last_name}
                            />
                            {fieldErrors.last_name && (
                              <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                                {fieldErrors.last_name}
                              </Form.Control.Feedback>
                            )}
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>
                  ) : (
                    <h2 className="mb-1">{formData.name} {formData.last_name}</h2>
                  )}
                  <p className="text-body-secondary mb-2">
                    <FontAwesomeIcon icon={faPhone} className="me-2" />
                    {isEditing ? (
                      <InputGroup className="d-inline-flex" style={{ width: '240px' }}>
                        <InputGroup.Text>+44</InputGroup.Text>
                        <Form.Control
                          type="tel"
                          name="phone"
                          value={normalizeUkPhoneLocal(formData.phone || '')}
                          onChange={e => {
                            const newValue = normalizeUkPhoneLocal(e.target.value);
                            setFormData({
                              ...formData,
                              phone: newValue
                            });
                            if (fieldErrors.phone) {
                              setFieldErrors({
                                ...fieldErrors,
                                phone: ''
                              });
                            }
                          }}
                          inputMode="numeric"
                          pattern="\\d{9,10}"
                          title="Enter 9-10 digits after +44"
                          isInvalid={!!fieldErrors.phone}
                        />
                      </InputGroup>
                    ) : (
                      profileData?.profile?.phone || 'N/A'
                    )}
                  </p>
                  <p className="text-body-secondary mb-0">
                    <FontAwesomeIcon icon={faMapPin} className="me-2" />
                    {profileData?.profile?.location || 'Not set'}
                  </p>
                </div>
                <div>
                  {!isEditing ? (
                    <Button
                      variant="phoenix-primary"
                      onClick={() => setIsEditing(true)}
                      startIcon={<FontAwesomeIcon icon={faEdit} />}
                      size="sm"
                    >
                      Edit Profile
                    </Button>
                  ) : (
                    <div className="d-flex gap-2">
                      <Button
                        variant="success"
                        onClick={handleSave}
                        loading={isSaving}
                        startIcon={<FontAwesomeIcon icon={faSave} />}
                        size="sm"
                      >
                        Save
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={handleCancel}
                        startIcon={<FontAwesomeIcon icon={faTimes} />}
                        size="sm"
                      >
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Professional Details Card */}
      <Card className="mb-4">
        <Card.Header className="bg-light">
          <Card.Title className="mb-0">
            <FontAwesomeIcon icon={faBriefcase} className="me-2 text-primary" />
            Professional Details
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group className="text-start">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={profileData?.user?.email || ''}
                  disabled
                  placeholder="Email"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="text-start">
                <Form.Label>
                  Address <span className="text-danger">*</span>
                </Form.Label>
                {isEditing ? (
                  <Form.Control
                    type="text"
                    name="address"
                    value={formData.address || ''}
                    onChange={handleInputChange}
                    placeholder="Street address"
                    isInvalid={!!fieldErrors.address}
                  />
                ) : (
                  <Form.Control
                    type="text"
                    value={formData.address || ''}
                    disabled
                  />
                )}
                {fieldErrors.address && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {fieldErrors.address}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="text-start">
                <Form.Label>
                  Qualifications <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  name="qualifications"
                  value={formData.qualifications || ''}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="Qualifications"
                  style={{ minHeight: '50px' }}
                  isInvalid={!!fieldErrors.qualifications}
                />
                {fieldErrors.qualifications && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {fieldErrors.qualifications}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="text-start">
                <Form.Label>
                  Years of Experience <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  name="experience_years"
                  value={formData.experience_years || ''}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="Years of Experience"
                  className="input-spin-none"
                  isInvalid={!!fieldErrors.experience_years}
                />
                {fieldErrors.experience_years && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {fieldErrors.experience_years}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="text-start">
                <Form.Label>Locum Role</Form.Label>
                {isEditing ? (
                  <Form.Select
                    name="locum_role"
                    value={formData?.locum_role || 'pharmacist'}
                    onChange={handleInputChange}
                  >
                    <option value="pharmacist">Pharmacist</option>
                    <option value="technician">Technician</option>
                    <option value="dispenser">Dispenser</option>
                  </Form.Select>
                ) : (
                  <Form.Control
                    type="text"
                    value={
                      formData?.locum_role === 'pharmacist'
                        ? 'Pharmacist'
                        : formData?.locum_role === 'technician'
                          ? 'Technician'
                          : 'Dispenser'
                    }
                    disabled
                  />
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="text-start">
                <Form.Label>GPhC Number</Form.Label>
                <Form.Control
                  type="text"
                  name="gphc_number"
                  value={formData.gphc_number || ''}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="GPhC Number"
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="text-start">
                <Form.Label>
                  City <span className="text-danger">*</span>
                </Form.Label>
                {isEditing ? (
                  <Form.Control
                    type="text"
                    name="city"
                    value={formData.city || ''}
                    onChange={handleInputChange}
                    placeholder="City"
                    isInvalid={!!fieldErrors.city}
                  />
                ) : (
                  <Form.Control type="text" value={formData.city || ''} disabled />
                )}
                {fieldErrors.city && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {fieldErrors.city}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="text-start">
                <Form.Label>
                  Postcode <span className="text-danger">*</span>
                </Form.Label>
                {isEditing ? (
                  <Form.Control
                    type="text"
                    name="zip_code"
                    value={formData.zip_code || ''}
                    onChange={handleInputChange}
                    placeholder="Postcode"
                    isInvalid={!!fieldErrors.zip_code}
                  />
                ) : (
                  <Form.Control type="text" value={formData.zip_code || ''} disabled />
                )}
                {fieldErrors.zip_code && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {fieldErrors.zip_code}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="text-start">
                <Form.Label>
                  Date of Birth <span className="text-danger">*</span>
                </Form.Label>
                {isEditing ? (
                  <Form.Control
                    type="date"
                    name="dob"
                    value={toInputDate(formData.dob) || ''}
                    onChange={handleInputChange}
                    placeholder="Date of Birth"
                    isInvalid={!!fieldErrors.dob}
                  />
                ) : (
                  <Form.Control type="text" value={formatDisplayDate(formData.dob)} disabled />
                )}
                {fieldErrors.dob && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {fieldErrors.dob}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="text-start">
                <Form.Label>
                  Gender <span className="text-danger">*</span>
                </Form.Label>
                {isEditing ? (
                  <Form.Select
                    name="gender"
                    value={formData.gender || ''}
                    onChange={handleInputChange}
                    isInvalid={!!fieldErrors.gender}
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </Form.Select>
                ) : (
                  <Form.Control type="text" value={formData.gender || ''} disabled />
                )}
                {fieldErrors.gender && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {fieldErrors.gender}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="text-start">
                <Form.Label>
                  Qualification Date <span className="text-danger">*</span>
                </Form.Label>
                {isEditing ? (
                  <Form.Control
                    type="date"
                    name="qualification_date"
                    value={toInputDate(formData.qualification_date) || ''}
                    onChange={handleInputChange}
                    isInvalid={!!fieldErrors.qualification_date}
                  />
                ) : (
                  <Form.Control type="text" value={formatDisplayDate(formData.qualification_date)} disabled />
                )}
                {fieldErrors.qualification_date && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {fieldErrors.qualification_date}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="text-start">
                <Form.Label>
                  Independent Prescriber <span className="text-danger">*</span>
                </Form.Label>
                {isEditing ? (
                  <>
                    <div
                      className="d-flex gap-3 align-items-center pt-2"
                      style={{ minHeight: '38px' }}
                    >
                      <Form.Check
                        type="radio"
                        id="independent-prescriber-yes-edit"
                        name="independent_prescriber"
                        value="1"
                        label="Yes"
                        checked={String(formData.independent_prescriber) === '1'}
                        onChange={handleInputChange}
                      />
                      <Form.Check
                        type="radio"
                        id="independent-prescriber-no-edit"
                        name="independent_prescriber"
                        value="0"
                        label="No"
                        checked={String(formData.independent_prescriber) === '0'}
                        onChange={handleInputChange}
                      />
                    </div>
                    {fieldErrors.independent_prescriber && (
                      <div style={{ color: '#dc3545', fontSize: '0.875em', marginTop: '0.25rem' }}>
                        {fieldErrors.independent_prescriber}
                      </div>
                    )}
                  </>
                ) : (
                  <Form.Control
                    type="text"
                    value={formData.independent_prescriber === 1 || formData.independent_prescriber === '1' ? 'Yes' : formData.independent_prescriber === 0 || formData.independent_prescriber === '0' ? 'No' : ''}
                    disabled
                  />
                )}
              </Form.Group>
            </Col>
            {/* <Col md={6}>
  <div className="form-icon-container">
    <FloatingLabel controlId="locum_role" label="Locum Role">
      <Form.Control
        type="text"
        value={
          formData?.locum_role === 'pharmacist'
            ? 'Pharmacist'
            : 'Technician'
        }
        disabled // ✅ always disabled
       
      />
    </FloatingLabel>
  </div>
</Col> */}
          </Row>
        </Card.Body>
      </Card>

      {/* Location Card */}
      <Card className="mb-4">
        <Card.Header className="bg-light">
          <Card.Title className="mb-0">
            <FontAwesomeIcon icon={faMapPin} className="me-2 text-primary" />
            Service Location
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              {isEditing ? (
                <div>

                  <LocationAutocomplete
                    value={formData.location || ''}
                    onChange={location => {
                      setFormData({ ...formData, location });
                      // Clear error when user selects location
                      if (fieldErrors.location) {
                        setFieldErrors({
                          ...fieldErrors,
                          location: ''
                        });
                      }
                    }}
                    onCoordinatesChange={setSelectedCoordinates}
                    label="Address"
                    placeholder="Search location..."
                    required={true}
                  />
                </div>
              ) : (
                <Form.Group className="text-start">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    type="text"
                    name="location"
                    value={formData.location || ''}
                    disabled
                    placeholder="Location"
                  />
                </Form.Group>
              )}
              {isEditing && fieldErrors.location && (
                <div style={{ color: '#dc3545', fontSize: '0.875em', marginTop: '0.25rem' }}>
                  {fieldErrors.location}
                </div>
              )}
            </Col>
            <Col md={6}>
              <Form.Group className="text-start">
                <Form.Label>Travel Distance (miles)</Form.Label>
                <Form.Control
                  type="number"
                  name="travel_distance"
                  value={formData.travel_distance || ''}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="Travel Distance"
                  className="input-spin-none"
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>


      {/* Professional References Card */}
      <Card className="mb-4">
        <Card.Header className="bg-light">
          <Card.Title className="mb-0">
            <FontAwesomeIcon icon={faIdCard} className="me-2 text-primary" />
            Professional References
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <h6 className="text-body-secondary mb-3">Reference 1</h6>
              <Form.Group className="text-start mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  name="ref_Name_1"
                  value={formData.ref_Name_1 || profileData?.user?.ref_Name_1 || ''}
                  onChange={isEditing ? handleInputChange : undefined}
                  disabled={!isEditing}
                  placeholder="Name"
                />
              </Form.Group>
              <Form.Group className="text-start mb-3">
                <Form.Label>Phone</Form.Label>
                <InputGroup>
                  <InputGroup.Text>+44</InputGroup.Text>
                  <Form.Control
                    type="tel"
                    name="ref_PhoneNumber_1"
                    value={normalizeUkPhoneLocal(
                      formData.ref_PhoneNumber_1 ||
                      profileData?.user?.ref_PhoneNumber_1 ||
                      ''
                    )}
                    onChange={
                      isEditing
                        ? e =>
                          setFormData({
                            ...formData,
                            ref_PhoneNumber_1: normalizeUkPhoneLocal(
                              e.target.value
                            )
                          })
                        : undefined
                    }
                    disabled={!isEditing}
                    placeholder="7XXXXXXXXX"
                    inputMode="numeric"
                    pattern="\\d{9,10}"
                    title="Enter 9-10 digits after +44"
                  />
                </InputGroup>
              </Form.Group>
              <Form.Group className="text-start">
                <Form.Label>Details</Form.Label>
                <Form.Control
                  as="textarea"
                  name="ref_Details_1"
                  value={formData.ref_Details_1 || profileData?.user?.ref_Details_1 || ''}
                  onChange={isEditing ? handleInputChange : undefined}
                  disabled={!isEditing}
                  placeholder="Details"
                  style={{ minHeight: '80px' }}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <h6 className="text-body-secondary mb-3">Reference 2</h6>
              <Form.Group className="text-start mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  name="ref_Name_2"
                  value={formData.ref_Name_2 || profileData?.user?.ref_Name_2 || ''}
                  onChange={isEditing ? handleInputChange : undefined}
                  disabled={!isEditing}
                  placeholder="Name"
                />
              </Form.Group>
              <Form.Group className="text-start mb-3">
                <Form.Label>Phone</Form.Label>
                <InputGroup>
                  <InputGroup.Text>+44</InputGroup.Text>
                  <Form.Control
                    type="tel"
                    name="ref_PhoneNumber_2"
                    value={normalizeUkPhoneLocal(
                      formData.ref_PhoneNumber_2 ||
                      profileData?.user?.ref_PhoneNumber_2 ||
                      ''
                    )}
                    onChange={
                      isEditing
                        ? e =>
                          setFormData({
                            ...formData,
                            ref_PhoneNumber_2: normalizeUkPhoneLocal(
                              e.target.value
                            )
                          })
                        : undefined
                    }
                    disabled={!isEditing}
                    placeholder="7XXXXXXXXX"
                    inputMode="numeric"
                    pattern="\\d{9,10}"
                    title="Enter 9-10 digits after +44"
                  />
                </InputGroup>
              </Form.Group>
              <Form.Group className="text-start">
                <Form.Label>Details</Form.Label>
                <Form.Control
                  as="textarea"
                  name="ref_Details_2"
                  value={formData.ref_Details_2 || profileData?.user?.ref_Details_2 || ''}
                  onChange={isEditing ? handleInputChange : undefined}
                  disabled={!isEditing}
                  placeholder="Details"
                  style={{ minHeight: '80px' }}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>



      {/* Documents Card */}
      <Card className='mb-5'>
        <Card.Header className="bg-light">
          <Card.Title className="mb-0">
            <FontAwesomeIcon icon={faFileAlt} className="me-2 text-primary" />
            Documents ({profileData.documents.length})
          </Card.Title>
        </Card.Header>
        <Card.Body>
          {profileData.documents.length > 0 ? (
            <div className="list-group">
              {profileData.documents.map((doc: any) => (
                <div
                  key={doc.id}
                  className="list-group-item d-flex justify-content-between align-items-center"
                >
                  <div>
                    <h6 className="mb-1 d-flex align-items-center">
                      <FontAwesomeIcon
                        icon={faCertificate}
                        className="me-2 text-warning"
                      />
                      {doc.doc_type.replace(/_/g, ' ').toUpperCase()}
                    </h6>
                    <small className="text-body-secondary">
                      {doc.document_name}
                    </small>
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      variant="phoenix-secondary"
                      size="sm"
                      startIcon={<FontAwesomeIcon icon={faEye} />}
                      onClick={() => handleDocumentView(doc)}
                    >
                      View
                    </Button>
                    <Button
                      variant="phoenix-primary"
                      size="sm"
                      startIcon={<FontAwesomeIcon icon={faDownload} />}
                      onClick={() => handleDocumentDownload(doc)}
                    >
                      Download
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Alert variant="info" className="mb-0">
              No documents uploaded yet
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Document Viewer Modal */}
      <Modal
        show={showDocViewer}
        onHide={() => setShowDocViewer(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedDocument?.doc_type.replace(/_/g, ' ').toUpperCase()}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDocument && (
            <div className="text-center">
              <p className="mb-3 text-body-secondary">
                {selectedDocument.document_name}
              </p>
              <div
                style={{
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  padding: '20px',
                  backgroundColor: '#f8f9fa',
                  minHeight: '400px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column'
                }}
              >
                <FontAwesomeIcon
                  icon={faFileAlt}
                  size="3x"
                  className="text-muted mb-3"
                />
                <p className="text-muted mb-2">
                  <strong>{selectedDocument.document_name}</strong>
                </p>
                <p className="text-muted small mb-4">
                  Click the download button to view or download this document.
                </p>
                <Button
                  variant="phoenix-primary"
                  startIcon={<FontAwesomeIcon icon={faDownload} />}
                  onClick={() => {
                    handleDocumentDownload(selectedDocument);
                  }}
                >
                  Open Document
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default LocumProfilePage;
